import {
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { getComponentTree, getSourceForElement } from "./fiber";
import type { ComponentInfo } from "./fiber";
import { type Editor, getEditor, openInIde } from "./ide";
import { ContextMenu } from "./ContextMenu";

type State = "idle" | "hover" | "select";

interface ClickToComponentProps {
  editor?: Editor | undefined;
}

const STYLE_ID = "click-to-component-styles";

function injectStyles(): void {
  if (document.getElementById(STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    [data-click-to-component-highlight] {
      outline: 2px solid #89b4fa !important;
      outline-offset: 2px !important;
      cursor: pointer !important;
    }
    [data-click-to-component-active] * {
      cursor: pointer !important;
    }
  `;
  document.head.appendChild(style);
}

function removeStyles(): void {
  document.getElementById(STYLE_ID)?.remove();
}

/**
 * Dev-only component that enables Option/Alt+Click to open component source in IDE.
 *
 * - Hold Option/Alt to highlight components on hover
 * - Option/Alt+Click opens the component in your IDE
 * - Option/Alt+Right-click shows a component tree popover
 *
 * Tree-shakes to nothing in production via import.meta.env.DEV guard.
 */
export function ClickToComponent({
  editor: editorProp,
}: ClickToComponentProps = {}): ReactElement | null {
  // Tree-shake in production — this entire component disappears
  if (!import.meta.env.DEV) return null;

  return <ClickToComponentDev editor={editorProp} />;
}

function ClickToComponentDev({
  editor: editorProp,
}: ClickToComponentProps): ReactElement | null {
  const editor = editorProp ?? getEditor();
  const [state, setState] = useState<State>("idle");
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const [menuPosition, setMenuPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [menuComponents, setMenuComponents] = useState<ComponentInfo[]>([]);
  const previousTarget = useRef<HTMLElement | null>(null);

  // Inject/remove global styles
  useEffect(() => {
    injectStyles();
    return removeStyles;
  }, []);

  // Highlight management
  useEffect(() => {
    if (state === "idle") {
      document.body.removeAttribute("data-click-to-component-active");
      previousTarget.current?.removeAttribute(
        "data-click-to-component-highlight",
      );
      previousTarget.current = null;
      return;
    }

    document.body.setAttribute("data-click-to-component-active", "");

    if (target && state === "hover") {
      previousTarget.current?.removeAttribute(
        "data-click-to-component-highlight",
      );
      target.setAttribute("data-click-to-component-highlight", "");
      previousTarget.current = target;
    }
  }, [state, target]);

  // Cleanup highlight on unmount
  useEffect(() => {
    return () => {
      previousTarget.current?.removeAttribute(
        "data-click-to-component-highlight",
      );
      document.body.removeAttribute("data-click-to-component-active");
    };
  }, []);

  const handleClose = useCallback(() => {
    setState("idle");
    setTarget(null);
    setMenuPosition(null);
    setMenuComponents([]);
  }, []);

  // Core event listeners
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent): void {
      // Alt/Option key activates hover mode
      if (e.key === "Alt" && state === "idle") {
        setState("hover");
      }
    }

    function handleKeyUp(e: KeyboardEvent): void {
      // Releasing Alt while hovering returns to idle (but not while menu is open)
      if (e.key === "Alt" && state === "hover") {
        setState("idle");
        setTarget(null);
      }
    }

    function handleMouseMove(e: MouseEvent): void {
      if (state !== "hover") return;
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (el instanceof HTMLElement) {
        setTarget(el);
      }
    }

    function handleClick(e: MouseEvent): void {
      if (state !== "hover") return;

      // Alt+Click → open in IDE
      if (e.altKey) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const el = document.elementFromPoint(e.clientX, e.clientY);
        if (el instanceof HTMLElement) {
          const source = getSourceForElement(el);
          if (source) {
            openInIde(editor, source);
          }
        }
        setState("idle");
        setTarget(null);
      }
    }

    function handleContextMenu(e: MouseEvent): void {
      // Alt+Right-click → show component tree
      if (!e.altKey) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (el instanceof HTMLElement) {
        const components = getComponentTree(el);
        if (components.length > 0) {
          setMenuComponents(components);
          setMenuPosition({ x: e.clientX, y: e.clientY });
          setState("select");
        }
      }
    }

    function handleBlur(): void {
      if (state === "hover") {
        setState("idle");
        setTarget(null);
      }
    }

    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("keyup", handleKeyUp, true);
    document.addEventListener("mousemove", handleMouseMove, true);
    document.addEventListener("click", handleClick, true);
    document.addEventListener("contextmenu", handleContextMenu, true);
    window.addEventListener("blur", handleBlur);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("keyup", handleKeyUp, true);
      document.removeEventListener("mousemove", handleMouseMove, true);
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("contextmenu", handleContextMenu, true);
      window.removeEventListener("blur", handleBlur);
    };
  }, [state, editor, handleClose]);

  if (state === "select" && menuPosition && menuComponents.length > 0) {
    return (
      <ContextMenu
        components={menuComponents}
        x={menuPosition.x}
        y={menuPosition.y}
        editor={editor}
        onClose={handleClose}
      />
    );
  }

  return null;
}
